import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SimpleMessageTemplate } from 'types/templates/simple-templates';
import * as simpleTemplatesApi from 'api/simple-templates';
import { dispatch } from 'store';
import { loadTemplateDefaultFiles } from 'store/slices/templates';

interface SimpleTemplatesState {
  templates: SimpleMessageTemplate[];
  loading: boolean;
  error: string | null;
}

// Hard-coded example template for demo purposes
const exampleTemplates: SimpleMessageTemplate[] = [
  {
    id: 'demo-1',
    name: 'ברכת בוקר טוב',
    content: 'בוקר טוב! 🌅\n\nאנו מקווים שתהיה לכם יום נפלא ומוצלח.\n\nצוות התמיכה שלנו זמין עבורכם בכל שאלה.',
    status: true, // Active
    tags: ['ברכה', 'בוקר', 'שירות לקוחות'],
    companyId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-2',
    name: 'תזכורת לפגישה',
    content: 'שלום רב,\n\nזו תזכורת ידידותית לפגישה שלנו מחר בשעה 10:00.\n\nנשמח לראותכם!\n\nבברכה,\nהצוות',
    status: true, // Active
    tags: ['תזכורת', 'פגישה'],
    companyId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-3',
    name: 'הודעת עדכון מערכת',
    content: 'עדכון חשוב! 🔔\n\nהמערכת שלנו תעבור שדרוג טכני היום בין השעות 22:00-23:00.\n\nנצטרך לזמינות מלאה לאחר מכן.\n\nתודה על ההבנה!',
    status: false, // Inactive
    tags: ['עדכון', 'תחזוקה', 'טכני'],
    companyId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const initialState: SimpleTemplatesState = {
  templates: exampleTemplates,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'simpleTemplates',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setTemplates(state, action: PayloadAction<SimpleMessageTemplate[]>) {
      state.templates = action.payload;
      state.loading = false;
      state.error = null;
    },
    addTemplate(state, action: PayloadAction<SimpleMessageTemplate>) {
      state.templates.unshift(action.payload);
    },
    updateTemplate(state, action: PayloadAction<SimpleMessageTemplate>) {
      const index = state.templates.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
    },
    removeTemplate(state, action: PayloadAction<string>) {
      state.templates = state.templates.filter((t) => t.id !== action.payload);
    },
  },
});

export default slice.reducer;

export const { setLoading, setError, setTemplates, addTemplate, updateTemplate, removeTemplate } = slice.actions;

// Async actions
export async function getSimpleTemplates() {
  try {
    dispatch(setLoading(true));
    //* קבצי ברירת המחדל (templates slice) נטענים במקביל — הפופאובר והמסך מציגים אותם לפי GUID
    void loadTemplateDefaultFiles();
    const templates = await simpleTemplatesApi.getSimpleTemplates();
    dispatch(setTemplates(templates));
  } catch (error) {
    console.error('getSimpleTemplates error:', error);
    dispatch(setError('שגיאה בטעינת התבניות'));
    dispatch(setLoading(false));
  }
}

export function createSimpleTemplateAction(params: Parameters<typeof simpleTemplatesApi.createSimpleTemplate>[0]) {
  return async () => {
    try {
      dispatch(setLoading(true));
      const result = await simpleTemplatesApi.createSimpleTemplate(params);
      if (result.result && result.template) {
        dispatch(addTemplate(result.template));
        return { success: true, template: result.template };
      } else {
        dispatch(setError(result.message || 'שגיאה ביצירת התבנית'));
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('createSimpleTemplateAction error:', error);
      dispatch(setError('שגיאה ביצירת התבנית'));
      return { success: false };
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function updateSimpleTemplateAction(params: Parameters<typeof simpleTemplatesApi.updateSimpleTemplate>[0]) {
  return async () => {
    try {
      dispatch(setLoading(true));
      const result = await simpleTemplatesApi.updateSimpleTemplate(params);
      if (result.result && result.template) {
        dispatch(updateTemplate(result.template));
        return { success: true, template: result.template };
      } else {
        dispatch(setError(result.message || 'שגיאה בעדכון התבנית'));
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('updateSimpleTemplateAction error:', error);
      dispatch(setError('שגיאה בעדכון התבנית'));
      return { success: false };
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function deleteSimpleTemplateAction(id: string) {
  return async () => {
    try {
      dispatch(setLoading(true));
      const result = await simpleTemplatesApi.deleteSimpleTemplate(id);
      if (result.result) {
        dispatch(removeTemplate(id));
        return { success: true };
      } else {
        dispatch(setError(result.message || 'שגיאה במחיקת התבנית'));
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('deleteSimpleTemplateAction error:', error);
      dispatch(setError('שגיאה במחיקת התבנית'));
      return { success: false };
    } finally {
      dispatch(setLoading(false));
    }
  };
}

